import { useMutation, useQuery } from '@tanstack/react-query'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'
import { useAddressesStore } from '@/store'
import { AddressesFilterParams, CreateAddressParams, UpdateAddressParams } from '@/types/address'

export const useUserAddresses = (
  userId: string,
  params: AddressesFilterParams = {},
  options?: { enabled?: boolean }
) => {
  const fetchAddresses = useAddressesStore(state => state.fetchAddresses)

  return useQuery({
    queryKey: ['user-addresses', userId, params],
    queryFn: () => fetchAddresses(userId, params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!userId && (options?.enabled ?? true)
  })
}

export const useCreateAddress = () => {
  const createAddress = useAddressesStore(state => state.createAddress)

  return useMutation({
    mutationFn: ({ userId, params }: { userId: string; params: CreateAddressParams }) => createAddress(userId, params),
    onSuccess: () => {}
  })
}

export const useUpdateAddress = () => {
  const updateAddress = useAddressesStore(state => state.updateAddress)

  return useMutation({
    mutationFn: ({ userId, addressId, params }: { userId: string; addressId: string; params: UpdateAddressParams }) =>
      updateAddress(userId, addressId, params),
    onSuccess: () => {}
  })
}

export const useDeleteAddress = () => {
  const deleteAddress = useAddressesStore(state => state.deleteAddress)

  return useMutation({
    mutationFn: ({ userId, addressId }: { userId: string; addressId: string }) => deleteAddress(userId, addressId),
    onSuccess: () => {}
  })
}

export const useBulkDeleteAddresses = () => {
  const bulkDeleteAddresses = useAddressesStore(state => state.bulkDeleteAddresses)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: ({ userId, ids }: { userId: string; ids: string[] }) => bulkDeleteAddresses(userId, ids),
    onSuccess: response => {
      handleBulkDeleteSuccess(response)
    }
  })
}

export const useSetPrimaryAddress = () => {
  const setPrimaryAddress = useAddressesStore(state => state.setPrimaryAddress)

  return useMutation({
    mutationFn: ({ userId, addressId }: { userId: string; addressId: string }) => setPrimaryAddress(userId, addressId),
    onSuccess: () => {}
  })
}
